/**
 * Qualitative Language System — Block 1
 *
 * Maps numeric map parameters to human-readable, sensation-based descriptions.
 * Users don't think in °C or % — they think in feelings, comparisons, and
 * lived experiences. Every label here answers: "what does this number feel like?"
 *
 * Covers all choropleth layer IDs from layers-config.ts.
 * Bilingual (en / ru) from the start so the map works regardless of locale.
 */

type LangKey = 'en' | 'ru';

interface QualLevel {
  /** Inclusive upper bound. If value ≤ maxValue this level matches. */
  maxValue: number;
  en: string;
  ru: string;
}

interface QualDef {
  levels: QualLevel[];
  /** Returned when value exceeds all level bounds. */
  fallback: { en: string; ru: string };
}

const VOCAB: Record<string, QualDef> = {
  // ─── Altitude ────────────────────────────────────────────────────────────
  altitude: {
    levels: [
      { maxValue: 200,  en: 'Flatlands — warm summers, humid valleys',             ru: 'Равнина — тёплое лето, влажные долины' },
      { maxValue: 500,  en: 'Foothills — mild and sheltered, light breezes',        ru: 'Предгорье — мягко и укрыто, лёгкий ветерок' },
      { maxValue: 1000, en: 'Highland — noticeably cooler, snow in winter',         ru: 'Нагорье — заметно прохладнее, снег зимой' },
      { maxValue: 2000, en: 'Mountain town — crisp air, cold winters, ski country', ru: 'Горный городок — свежий воздух, холодные зимы' },
      { maxValue: 3500, en: 'Alpine — short summers, snow 5+ months',               ru: 'Высокогорье — короткое лето, снег 5+ месяцев' },
    ],
    fallback: { en: 'Extreme altitude', ru: 'Экстремальная высота' },
  },

  // ─── Temperature (annual mean) ────────────────────────────────────────────
  t_mean: {
    levels: [
      { maxValue: 3,  en: 'Cold — long winters with frost, like Helsinki',          ru: 'Холодно — долгая зима с морозами, как в Хельсинки' },
      { maxValue: 8,  en: 'Cool — four clear seasons, like Munich or Warsaw',       ru: 'Прохладно — четыре выраженных сезона, как в Варшаве' },
      { maxValue: 13, en: 'Mild — comfortable year-round, like northern Italy',     ru: 'Умеренно — комфортно круглый год, как в Северной Италии' },
      { maxValue: 17, en: 'Warm — like Barcelona, hot summers, mild winters',       ru: 'Тепло — как в Барселоне, жаркое лето, мягкая зима' },
      { maxValue: 22, en: 'Hot — air conditioning in summer, like southern Italy',  ru: 'Жарко — кондиционер летом, как в Южной Италии' },
    ],
    fallback: { en: 'Very hot — like UAE, AC is essential', ru: 'Очень жарко — как в ОАЭ, без кондиционера тяжело' },
  },

  // ─── July temperature ─────────────────────────────────────────────────────
  t_jul: {
    levels: [
      { maxValue: 14, en: 'Cool summer — sweater in July, like Scotland',           ru: 'Прохладное лето — свитер в июле, как в Шотландии' },
      { maxValue: 19, en: 'Mild summer — t-shirt weather, easy outdoor life',       ru: 'Мягкое лето — футболка, приятные прогулки' },
      { maxValue: 24, en: 'Warm summer — sunny and pleasant, occasional hot days',  ru: 'Тёплое лето — солнечно и приятно, редкая жара' },
      { maxValue: 29, en: 'Hot summer — 25-30°C, plan for shade and water',         ru: 'Жаркое лето — 25-30°C, нужна тень и вода' },
    ],
    fallback: { en: 'Very hot summer — regularly above 30°C', ru: 'Очень жаркое лето — регулярно выше 30°C' },
  },

  // ─── January temperature ──────────────────────────────────────────────────
  t_jan: {
    levels: [
      { maxValue: -6, en: 'Severe winter — deep frost, heavy snowfall',             ru: 'Суровая зима — сильные морозы, обильный снег' },
      { maxValue: 0,  en: 'Cold winter — frost, lasting snow cover',                ru: 'Холодная зима — морозы, устойчивый снег' },
      { maxValue: 4,  en: 'Moderate winter — occasional snow and frost',            ru: 'Умеренная зима — редкий снег, иногда заморозки' },
      { maxValue: 9,  en: 'Mild winter — light jacket, snow is rare',               ru: 'Мягкая зима — лёгкая куртка, снег редкость' },
    ],
    fallback: { en: 'Warm winter — Mediterranean, frost-free', ru: 'Тёплая зима — средиземноморская, без заморозков' },
  },

  // ─── Humidity ─────────────────────────────────────────────────────────────
  humidity: {
    levels: [
      { maxValue: 50, en: 'Very dry — desert-feel, skin may need moisturiser',      ru: 'Очень сухо — пустынное ощущение, нужно увлажнение кожи' },
      { maxValue: 60, en: 'Dry and fresh — crisp mountain air',                     ru: 'Сухо и свежо — типичный горный воздух' },
      { maxValue: 68, en: 'Comfortable — standard for continental Europe',          ru: 'Комфортно — норма для континентальной Европы' },
      { maxValue: 76, en: 'Humid — clothes feel heavier, like Amsterdam',           ru: 'Влажно — одежда кажется тяжелее, как в Амстердаме' },
    ],
    fallback: { en: 'Very humid — muggy and heavy, tropical feel', ru: 'Очень влажно — парная атмосфера, тропическое ощущение' },
  },

  // ─── Annual precipitation ─────────────────────────────────────────────────
  precip: {
    levels: [
      { maxValue: 400,  en: 'Dry — little rain, sunny most days',                   ru: 'Сухо — мало дождей, солнечно большую часть года' },
      { maxValue: 700,  en: 'Moderate — rain spreads through the year',             ru: 'Умеренно — дождь равномерно в течение года' },
      { maxValue: 1000, en: 'Rainy — umbrella is your friend, like London',         ru: 'Дождливо — зонт в кармане обязателен, как в Лондоне' },
      { maxValue: 1400, en: 'Very rainy — like Alpine foothills, lush green',       ru: 'Очень дождливо — как в альпийских предгорьях, зелено' },
    ],
    fallback: { en: 'Wet — consistently rainy, deep green landscape', ru: 'Мокро — стабильно дождливо, насыщенная зелень' },
  },

  // ─── Snow days ────────────────────────────────────────────────────────────
  snow: {
    levels: [
      { maxValue: 1,  en: 'No snow — almost never',                                 ru: 'Снег не бывает или почти никогда' },
      { maxValue: 7,  en: 'Rare snow — once or twice per winter, melts fast',       ru: 'Редкий снег — раз-два за зиму, тает быстро' },
      { maxValue: 20, en: 'Occasional snow — a few weeks with snow cover',          ru: 'Снег бывает — несколько зимних недель со снегом' },
      { maxValue: 45, en: 'Snowy winters — like Munich or Vienna',                  ru: 'Снежные зимы — как в Мюнхене или Вене' },
      { maxValue: 80, en: 'Snow season — 2-3 months of solid snow cover',           ru: 'Снежный сезон — 2-3 месяца снежного покрова' },
    ],
    fallback: { en: 'Alpine winter — snow November through April', ru: 'Альпийская зима — снег с ноября по апрель' },
  },

  // ─── Sunny days ───────────────────────────────────────────────────────────
  sunny: {
    levels: [
      { maxValue: 100, en: 'Overcast — grey skies often, like Scotland',            ru: 'Пасмурно — серое небо часто, как в Шотландии' },
      { maxValue: 130, en: 'Little sun — like London or Hamburg',                   ru: 'Мало солнца — как в Лондоне или Гамбурге' },
      { maxValue: 160, en: 'Mixed — reasonable sun, typical Central Europe',        ru: 'Смешанно — достаточно солнца, типичная Центральная Европа' },
      { maxValue: 190, en: 'Sunny — like northern Italy or Vienna',                 ru: 'Солнечно — как в Северной Италии или Вене' },
      { maxValue: 230, en: 'Very sunny — like Tuscany or the Adriatic coast',       ru: 'Очень солнечно — как в Тоскане или на Адриатике' },
    ],
    fallback: { en: 'Mediterranean sun — 250+ bright days a year', ru: 'Средиземноморское солнце — 250+ ярких дней в году' },
  },

  // ─── Internet ─────────────────────────────────────────────────────────────
  internet: {
    levels: [
      { maxValue: 10,  en: 'Slow — video calls may be unreliable',                  ru: 'Медленно — видеозвонки могут зависать' },
      { maxValue: 30,  en: 'Basic — HD video ok, large downloads are slow',         ru: 'Базовый — HD-видео в порядке, скачивание медленное' },
      { maxValue: 80,  en: 'Good — comfortable for remote work',                    ru: 'Хороший — комфортно для удалённой работы' },
      { maxValue: 300, en: 'Fast — smooth for everything',                          ru: 'Быстрый — плавная работа с любым контентом' },
    ],
    fallback: { en: 'Very fast — fiber-class connection', ru: 'Очень быстрый — уровень оптоволокна' },
  },

  // ─── Hospital distance ────────────────────────────────────────────────────
  hospital: {
    levels: [
      { maxValue: 5,  en: 'Hospital nearby — under 10 minutes',                     ru: 'Больница рядом — меньше 10 минут' },
      { maxValue: 15, en: 'Accessible — 15-20 minute drive',                        ru: 'Доступно — 15-20 минут на машине' },
      { maxValue: 30, en: 'Moderate — about 30 minutes away',                       ru: 'Умеренно — около 30 минут езды' },
      { maxValue: 60, en: 'Remote — 40-60 min in an emergency',                     ru: 'Удалённо — 40-60 минут в экстренном случае' },
    ],
    fallback: { en: 'Very remote — over an hour to hospital', ru: 'Очень удалённо — больше часа до больницы' },
  },

  // ─── Train station distance ───────────────────────────────────────────────
  train: {
    levels: [
      { maxValue: 2,  en: 'Walking distance to station',                            ru: 'Пешком до станции' },
      { maxValue: 8,  en: 'Nearby — bike or short taxi ride',                       ru: 'Недалеко — велосипед или такси' },
      { maxValue: 20, en: 'Moderate — car needed to reach station',                 ru: 'Умеренно — нужна машина до станции' },
      { maxValue: 50, en: 'Far from rail — car-dependent life',                     ru: 'Далеко от ж/д — зависимость от машины' },
    ],
    fallback: { en: 'No rail access — fully car-dependent', ru: 'Нет ж/д доступа — только на машине' },
  },

  // ─── Rent ─────────────────────────────────────────────────────────────────
  rent: {
    levels: [
      { maxValue: 35, en: 'Very affordable — rural Italy pricing',                  ru: 'Очень доступно — провинциальная Италия' },
      { maxValue: 55, en: 'Affordable — small town market',                         ru: 'Доступно — ценник небольшого города' },
      { maxValue: 75, en: 'Moderate — secondary city range',                        ru: 'Умеренно — вторичные города' },
      { maxValue: 100, en: 'Above average — popular or scenic areas',               ru: 'Выше среднего — популярные или живописные районы' },
    ],
    fallback: { en: 'Premium — major city centers or Lake Como', ru: 'Дорого — центры крупных городов или Озёрная Комо' },
  },

  // ─── Sale price ───────────────────────────────────────────────────────────
  sale: {
    levels: [
      { maxValue: 800,  en: 'Very cheap — rural, limited resale market',            ru: 'Очень дёшево — сельская местность, ограниченный рынок' },
      { maxValue: 1200, en: 'Affordable — small towns, good value',                 ru: 'Доступно — небольшие города, хорошая ценность' },
      { maxValue: 2000, en: 'Moderate — mid-size towns, typical Italy',             ru: 'Умеренно — средние городки, типичная Италия' },
      { maxValue: 3500, en: 'Above average — desirable locations',                  ru: 'Выше среднего — привлекательные локации' },
    ],
    fallback: { en: 'Premium — Lake Como, Dolomites, city centers', ru: 'Премиум — Озёрная Комо, Доломиты, центры городов' },
  },

  // ─── Population density ───────────────────────────────────────────────────
  density: {
    levels: [
      { maxValue: 50,   en: 'Very sparse — countryside, few neighbours',            ru: 'Малолюдно — сельская местность, мало соседей' },
      { maxValue: 150,  en: 'Low density — quiet small town feel',                  ru: 'Низкая плотность — тихий маленький город' },
      { maxValue: 400,  en: 'Moderate — lively without being crowded',              ru: 'Умеренно — живо, но не тесно' },
      { maxValue: 1000, en: 'Dense — urban energy, city amenities nearby',          ru: 'Плотно — городская энергия, всё рядом' },
    ],
    fallback: { en: 'Very dense — metropolitan core', ru: 'Очень плотно — центр мегаполиса' },
  },

  // ─── Tax (addizionale IRPEF) ──────────────────────────────────────────────
  irpef: {
    levels: [
      { maxValue: 0,   en: 'Zero local tax — maximum take-home',                   ru: 'Нулевой местный налог — максимальный доход на руки' },
      { maxValue: 0.4, en: 'Low local surcharge — tax-efficient comune',           ru: 'Низкая надбавка — налогово эффективный коммуне' },
      { maxValue: 0.7, en: 'Average local surcharge',                              ru: 'Средняя местная надбавка' },
      { maxValue: 1.0, en: 'Above average — notable municipal tax',                ru: 'Выше среднего — заметный муниципальный налог' },
    ],
    fallback: { en: 'High municipal surcharge', ru: 'Высокая муниципальная надбавка' },
  },
};

/**
 * Returns a human-readable qualitative description for a map layer value.
 *
 * @param layerId  - choropleth layer ID from layers-config.ts
 * @param value    - numeric value to describe
 * @param lang     - 'en' or 'ru', defaults to 'ru'
 * @returns        - qualitative string, or null if layer is not in vocabulary
 */
export function qualitativeLabel(
  layerId: string,
  value: number | null | undefined,
  lang: LangKey = 'ru',
): string | null {
  if (value == null || !isFinite(value)) return null;
  const def = VOCAB[layerId];
  if (!def) return null;
  const level = def.levels.find((l) => value <= l.maxValue);
  return (level ?? def.fallback)[lang];
}
